import { createSlice } from '@reduxjs/toolkit';
import {
  getAudioDevices,
  getCameraDevice,
  unMuteAudio,
  muteAudio,
  muteVideo,
  unMuteVideo,
  switchMicPhone,
  switchCamera,
  switchDefaultMic,
} from './media.thunk';
import type { AppState } from '../index';

/**
 * @description 会议相关内容
 */

interface MeDiaState {
  /** @description 麦克风Id */
  micId: string;
  /** @description 视频Id */
  cameraId: string;
  /**
   * @description 音频开启状态
   */
  micStatus: boolean;
  /** @description 麦克风操作loading */
  micLoading: boolean;
  /** @description 摄像头操作loading */
  cameraLoading: boolean;
  /** @description 麦克风列表 */
  micList: any[];
  /** @description 扬声器列表 */
  speakerList: any[];
  /** @description 音频等级 */
  audioLevel: number;
  /** @description 选择音频列表状态 */
  audioDeviceListVisible: boolean;
  /** @description. 选择摄像头列表状态 */
  cameraListVisible: boolean;
  /** @description  摄像头状态 */
  cameraStatus: boolean;
  /** @description 摄像头list */
  cameraList: any[];
  /** @description 是否允许访问摄像头 */
  allowCamera: boolean;
  /** @description 是否允许访问麦克风 */
  allowAudio: boolean;
  /** @description 静音时检测音量用的本地音频流 */
  localAudioStream: any;
}

const initialState: MeDiaState = {
  micId: '',
  cameraId: '',
  micStatus: false,
  micLoading: false,
  cameraLoading: false,
  micList: [],
  speakerList: [],
  cameraList: [],
  audioLevel: 1,
  audioDeviceListVisible: false,
  cameraListVisible: false,
  cameraStatus: false,
  allowAudio: false,
  allowCamera: false,

  localAudioStream: null,
};

export const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    changeAudioState: (state, action) => {
      state.micStatus = action.payload;
    },
    changeAudioListVisible: (state, action) => {
      state.audioDeviceListVisible = action.payload;
    },
    changeCameraState: (state, action) => {
      state.cameraStatus = action.payload;
    },
    changeCameraListVisible: (state, action) => {
      state.cameraListVisible = action.payload;
    },
    setLocalAudioStream: (state, action) => {
      state.localAudioStream = action.payload;
    },
    changeMicLoading: (state, action) => {
      state.micLoading = action.payload;
    },
    changeCameraLoading: (state, action) => {
      state.cameraLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAudioDevices.fulfilled, (state, action) => {
      state.micList = action.payload?.microphones ?? [];
      state.speakerList = action.payload?.speakers ?? [];
      state.allowAudio = !!action.payload?.allowAudio;
    });

    builder.addCase(getCameraDevice.fulfilled, (state, action) => {
      const defaultCameraId = action?.payload?.defaultCameraId === '' ? state.micId : action?.payload?.defaultCameraId;
      const cameraList = action?.payload?.cameraList ?? [];
      state.cameraId = defaultCameraId;
      state.cameraList = cameraList;
      state.allowCamera = !!action.payload?.allowCamera;
    });

    builder.addCase(unMuteAudio.fulfilled, (state, action) => {
      state.micStatus = action.payload;
      state.micLoading = false;
    });

    builder.addCase(muteAudio.fulfilled, (state) => {
      state.micStatus = false;
      state.micLoading = false;
    });

    builder.addCase(unMuteVideo.fulfilled, (state, action) => {
      state.cameraStatus = !!action.payload;
      state.cameraLoading = false;
    });

    builder.addCase(muteVideo.fulfilled, (state, action) => {
      state.cameraStatus = !!action.payload;
      state.cameraLoading = false;
    });

    builder.addCase(switchMicPhone.fulfilled, (state, action) => {
      if (action.payload) {
        state.micId = action.payload;
      }
    });

    builder.addCase(switchCamera.fulfilled, (state, action) => {
      if (action.payload) {
        state.cameraId = action.payload;
      }
    });

    builder.addCase(switchDefaultMic.fulfilled, (state, action) => {
      if (action.payload === '') {
        state.micId = action.payload;
      }
    });
  },
});

export const getMicStatus = (state: AppState) => state.media.micStatus;

export const getMicId = (state: AppState) => state.media.micId;

export const getCameraId = (state: AppState) => state.media.cameraId;

export const getCameraStatus = (state: AppState) => state.media.cameraStatus;

export const getAudioLevel = (state: AppState) => state.media.audioLevel;

export const getLocalAudioStream = (state: AppState) => state.media.localAudioStream;

export const {
  changeAudioState,
  changeCameraState,
  changeAudioListVisible,
  changeCameraListVisible,
  setLocalAudioStream,
  changeMicLoading,
  changeCameraLoading,
} = mediaSlice.actions;

export default mediaSlice.reducer;
